import React from 'react';
import { FiRefreshCw, FiTrash2 } from 'react-icons/fi';
import { schedulerApiFetch } from '../../api/schedulerClient';

const previewCache = new Map();
const MAX_CACHE_ENTRIES = 6;
const DASH = '—';

function savePreviewCache(key, value) {
  if (previewCache.has(key)) previewCache.delete(key);
  previewCache.set(key, value);
  while (previewCache.size > MAX_CACHE_ENTRIES) {
    const oldest = previewCache.keys().next().value;
    previewCache.delete(oldest);
  }
}

function rowKey(row) {
  return String(row?.fleet_id || '');
}

function isAmbTitle(title) {
  const text = String(title || '').trim().toLowerCase();
  if (!text || text.includes('bpp') || text.includes('bbp')) return false;
  return text.includes('amb');
}

function columnMax(row, losId) {
  const cell = (row?.los_capacity || []).find((item) => item.los_id === losId);
  if (!cell || cell.max === null || cell.max === undefined) return 1;
  return Number(cell.max);
}

function ceilingMap(row) {
  const map = {};
  (row?.los_capacity || []).forEach((cell) => {
    map[cell.los_id] = cell.max === 0 || cell.max ? Number(cell.max) : 1;
  });
  return map;
}

function normalizeRow(row) {
  return {
    ...row,
    los_capacity: Array.isArray(row.los_capacity) ? row.los_capacity : [],
    groups: Array.isArray(row.groups) ? row.groups : [],
    funding_names: row.funding_names || [],
  };
}

function choicesFor(row, catalog) {
  const list = Array.isArray(catalog) ? catalog.filter((los) => los?.los_id) : [];
  if (list.length) return list;
  if (!row?.los_id) return [];
  return [{ los_id: row.los_id, los_title: row.los_title || row.los_id }];
}

function titleFor(losId, row, catalog) {
  const found = choicesFor(row, catalog).find((los) => los.los_id === losId);
  if (found?.los_title) return found.los_title;
  if (row?.los_id === losId) return row.los_title || losId;
  return losId;
}

function defaultLine(row, catalog) {
  if (!row?.los_id) return [];
  const own = { los_id: row.los_id, count: 0 };
  if (isAmbTitle(row.los_title)) return [own];
  const amb = choicesFor(row, catalog).find((los) => isAmbTitle(los.los_title) && los.los_id !== row.los_id);
  if (!amb) return [own];
  return [own, { los_id: amb.los_id, count: 0 }];
}

function linesFromGroups(groups) {
  return (groups || []).map((set) => (
    Object.entries(set || {}).map(([los_id, count]) => ({
      los_id,
      count: Number(count) || 0,
    }))
  ));
}

function groupsToSave(lines) {
  return (lines || []).map((line) => {
    const set = {};
    (line || []).forEach((chip) => {
      const count = Number(chip.count) || 0;
      if (chip.los_id && count > 0) set[chip.los_id] = count;
    });
    return set;
  }).filter((set) => Object.keys(set).length > 0);
}

function clampLines(lines, ceilings) {
  return (lines || []).map((line) => line.map((chip) => {
    const ceiling = ceilings[chip.los_id];
    if (ceiling === undefined || !(Number(chip.count) > ceiling)) return chip;
    return { ...chip, count: ceiling };
  }));
}

function clampSaved(groups, ceilings) {
  return (groups || []).map((set) => {
    const next = { ...set };
    Object.keys(next).forEach((losId) => {
      const ceiling = ceilings[losId];
      if (ceiling === undefined) return;
      if (Number(next[losId]) > ceiling) next[losId] = ceiling;
      if (!(Number(next[losId]) > 0)) delete next[losId];
    });
    return next;
  }).filter((set) => Object.keys(set).length > 0);
}

function FundingFilter({ names, selected, onChange }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  const label = selected.length ? selected.join(', ') : 'All funding sources';
  const toggle = (name) => {
    onChange(selected.includes(name) ? selected.filter((item) => item !== name) : [...selected, name]);
  };
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="w-full sm:w-72 px-3 py-2 text-sm text-left bg-white border border-gray-200 rounded-lg text-gray-700"
      >
        <span className="block truncate">{label}</span>
      </button>
      {open ? (
        <div className="absolute z-20 mt-1 w-72 max-h-64 overflow-auto bg-white border border-gray-200 rounded-lg shadow-lg">
          {names.length === 0 ? (
            <p className="px-3 py-2 text-sm text-gray-400">No funding sources</p>
          ) : names.map((name) => (
            <label key={name} className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">
              <input
                type="checkbox"
                checked={selected.includes(name)}
                onChange={() => toggle(name)}
              />
              <span className="truncate">{name}</span>
            </label>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function GroupEditor({ row, catalog, lines, onChange }) {
  const options = choicesFor(row, catalog);
  const ceilings = ceilingMap(row);
  const updateLine = (index, line) => {
    const next = lines.map((item, itemIndex) => (itemIndex === index ? line : item));
    onChange(next);
  };
  return (
    <div className="space-y-3">
      {lines.map((line, index) => {
        const used = new Set(line.map((chip) => chip.los_id));
        const nextLos = options.find((los) => !used.has(los.los_id));
        const hints = line
          .filter((chip) => ceilings[chip.los_id] !== undefined)
          .map((chip) => `${titleFor(chip.los_id, row, catalog)} combo max is ${ceilings[chip.los_id]}`);
        return (
          <div key={index} className="flex flex-wrap items-center gap-2">
            {line.map((chip, chipIndex) => {
              const chipCeiling = ceilings[chip.los_id] === undefined ? null : ceilings[chip.los_id];
              const selectable = options.filter((los) => los.los_id === chip.los_id || !used.has(los.los_id));
              if (!selectable.some((los) => los.los_id === chip.los_id)) {
                selectable.unshift({ los_id: chip.los_id, los_title: titleFor(chip.los_id, row, catalog) });
              }
              return (
                <div key={`${chip.los_id}-${chipIndex}`} className="inline-flex items-center gap-1.5 shrink-0">
                  <select
                    aria-label={`LOS ${index + 1} ${chipIndex + 1}`}
                    value={chip.los_id}
                    onChange={(event) => {
                      const losId = event.target.value;
                      let count = Number(chip.count) || 0;
                      if (ceilings[losId] !== undefined && count > ceilings[losId]) count = ceilings[losId];
                      const next = line.map((item, itemIndex) => (
                        itemIndex === chipIndex ? { los_id: losId, count } : item
                      ));
                      updateLine(index, next);
                    }}
                    className="no-theme !w-auto min-w-[6.5rem] h-9 pl-3 pr-8 py-0 text-sm bg-white border border-gray-200 rounded-lg text-gray-800"
                  >
                    {selectable.map((los) => (
                      <option key={los.los_id} value={los.los_id}>{los.los_title || los.los_id}</option>
                    ))}
                  </select>
                  <input
                    type="number"
                    min="0"
                    max={chipCeiling === null ? undefined : chipCeiling}
                    step="1"
                    aria-label={`${titleFor(chip.los_id, row, catalog)} count`}
                    value={String(chip.count ?? 0)}
                    onChange={(event) => {
                      const raw = event.target.value;
                      if (!/^\d+$/.test(raw)) return;
                      let count = parseInt(raw, 10);
                      if (chipCeiling !== null && count > chipCeiling) count = chipCeiling;
                      const next = line.map((item, itemIndex) => (
                        itemIndex === chipIndex ? { ...item, count } : item
                      ));
                      updateLine(index, next);
                    }}
                    className="!w-14 h-9 px-2 py-0 text-sm text-center bg-white border border-gray-200 rounded-lg shrink-0"
                  />
                </div>
              );
            })}
            <button
              type="button"
              disabled={!nextLos}
              onClick={() => updateLine(index, [...line, { los_id: nextLos.los_id, count: 0 }])}
              className="h-9 px-2 text-xs font-semibold text-gray-600 disabled:text-gray-300"
            >
              + LOS
            </button>
            <button
              type="button"
              aria-label="Delete combination"
              onClick={() => {
                if (line.length > 1) {
                  updateLine(index, line.slice(0, -1));
                  return;
                }
                const next = lines.filter((_, itemIndex) => itemIndex !== index);
                onChange(next.length ? next : [defaultLine(row, catalog)]);
              }}
              className="h-9 w-9 flex items-center justify-center text-gray-400 hover:text-gray-700"
            >
              <FiTrash2 className="w-4 h-4" />
            </button>
            {hints.length ? (
              <span className="text-[11px] font-medium text-gray-400">{hints.join(' · ')}</span>
            ) : null}
          </div>
        );
      })}
      <button
        type="button"
        onClick={() => onChange([...lines, defaultLine(row, catalog)])}
        className="text-xs font-semibold tracking-wide text-gray-500 hover:text-gray-800"
      >
        + COMBINATION
      </button>
    </div>
  );
}

export default function CapPlanPanel({ getAuthToken, companyId, onFlushReady }) {
  const cacheKey = String(companyId || 'company');
  const cached = previewCache.get(cacheKey);
  const [rows, setRows] = React.useState((cached?.fleets || []).map(normalizeRow));
  const [losCatalog, setLosCatalog] = React.useState(cached?.los || []);
  const [losLoaded, setLosLoaded] = React.useState(cached ? cached.los_loaded !== false : true);
  const [fundingNames, setFundingNames] = React.useState(cached?.funding_names || []);
  const [loading, setLoading] = React.useState(!cached);
  const [error, setError] = React.useState('');
  const [search, setSearch] = React.useState('');
  const [funding, setFunding] = React.useState([]);
  const [saveState, setSaveState] = React.useState('Autosave');
  const [duplicateIds, setDuplicateIds] = React.useState(!!cached?.duplicate_fleet_ids);
  const [openId, setOpenId] = React.useState('');
  const [drafts, setDrafts] = React.useState({});
  const pendingRef = React.useRef({});
  const timerRef = React.useRef(null);
  const mountedRef = React.useRef(true);
  const loadGenRef = React.useRef(0);
  const getAuthTokenRef = React.useRef(getAuthToken);
  const rowsRef = React.useRef(rows);

  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      loadGenRef.current += 1;
    };
  }, []);

  React.useEffect(() => {
    getAuthTokenRef.current = getAuthToken;
  }, [getAuthToken]);
  React.useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  const flush = React.useCallback(async () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const batch = pendingRef.current;
    const fleets = Object.values(batch);
    if (!fleets.length) return;
    pendingRef.current = {};
    if (mountedRef.current) setSaveState('Saving…');
    try {
      const token = await getAuthTokenRef.current?.();
      const response = await schedulerApiFetch('cap-plan', {
        method: 'PUT',
        bearerToken: token,
        body: { fleets },
      });
      await response.json().catch(() => ({}));
      if (!response.ok) {
        pendingRef.current = {
          ...fleets.reduce((acc, item) => {
            acc[item.fleet_id] = item;
            return acc;
          }, {}),
          ...pendingRef.current,
        };
        if (mountedRef.current) setSaveState('Save failed');
        return;
      }
      if (mountedRef.current) setSaveState('Saved');
      const current = previewCache.get(cacheKey);
      if (current) savePreviewCache(cacheKey, { ...current, fleets: rowsRef.current });
    } catch (err) {
      pendingRef.current = {
        ...fleets.reduce((acc, item) => {
          acc[item.fleet_id] = item;
          return acc;
        }, {}),
        ...pendingRef.current,
      };
      if (mountedRef.current) setSaveState('Save failed');
    }
  }, [cacheKey]);

  React.useEffect(() => {
    onFlushReady?.(flush);
    return () => {
      onFlushReady?.(null);
      flush();
    };
  }, [flush, onFlushReady]);

  const load = React.useCallback(async (force) => {
    const gen = loadGenRef.current + 1;
    loadGenRef.current = gen;
    if (!force && previewCache.has(cacheKey)) {
      const hit = previewCache.get(cacheKey);
      if (!mountedRef.current) return;
      setRows((hit.fleets || []).map(normalizeRow));
      setLosCatalog(hit.los || []);
      setLosLoaded(hit.los_loaded !== false);
      setFundingNames(hit.funding_names || []);
      setDuplicateIds(!!hit.duplicate_fleet_ids);
      setLoading(false);
      setError('');
      return;
    }
    if (mountedRef.current) {
      setLoading(true);
      setError('');
    }
    try {
      const token = await getAuthTokenRef.current?.();
      const response = await schedulerApiFetch('cap-plan/preview', {
        bearerToken: token,
        cacheBust: true,
      });
      const payload = await response.json().catch(() => ({}));
      if (!mountedRef.current || loadGenRef.current !== gen) return;
      if (!response.ok || payload.success === false) {
        throw new Error(payload.detail || payload.message || 'Could not load Cap Plan');
      }
      const fleets = (Array.isArray(payload.fleets) ? payload.fleets : []).map(normalizeRow);
      const los = Array.isArray(payload.los) ? payload.los : [];
      const names = Array.isArray(payload.funding_names) ? payload.funding_names : [];
      const loaded = payload.los_loaded !== false;
      savePreviewCache(cacheKey, {
        fleets,
        los,
        los_loaded: loaded,
        funding_names: names,
        duplicate_fleet_ids: !!payload.duplicate_fleet_ids,
      });
      setRows(fleets);
      setLosCatalog(los);
      setLosLoaded(loaded);
      setFundingNames(names);
      setDuplicateIds(!!payload.duplicate_fleet_ids);
      setDrafts({});
      setOpenId('');
    } catch (err) {
      if (mountedRef.current && loadGenRef.current === gen) {
        setError(err?.message || 'Could not load Cap Plan');
      }
    } finally {
      if (mountedRef.current && loadGenRef.current === gen) {
        setLoading(false);
      }
    }
  }, [cacheKey]);

  React.useEffect(() => {
    load(false);
  }, [load]);

  const queueSave = (row, patch) => {
    if (!mountedRef.current) return;
    const nextRow = { ...row, ...patch, saved: true };
    const nextRows = rowsRef.current.map((item) => (rowKey(item) === rowKey(row) ? nextRow : item));
    rowsRef.current = nextRows;
    setRows(nextRows);
    const current = previewCache.get(cacheKey);
    if (current) savePreviewCache(cacheKey, { ...current, fleets: nextRows });
    pendingRef.current[rowKey(row)] = {
      fleet_id: nextRow.fleet_id,
      los_capacity: (nextRow.los_capacity || []).map((cell) => ({
        los_id: cell.los_id,
        los_title: cell.los_title || '',
        max: cell.max === undefined ? null : cell.max,
      })),
      groups: nextRow.groups || [],
    };
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flush, 700);
    setSaveState('Autosave');
  };

  const changeMax = (row, losId, max) => {
    const key = rowKey(row);
    const los_capacity = (row.los_capacity || []).map((cell) => (
      cell.los_id === losId ? { ...cell, max } : cell
    ));
    const nextRow = { ...row, los_capacity };
    const ceilings = ceilingMap(nextRow);
    const groups = clampSaved(row.groups, ceilings);
    setDrafts((current) => {
      if (!current[key]) return current;
      return { [key]: clampLines(current[key], ceilings) };
    });
    queueSave(row, { los_capacity, groups });
  };

  const changeGroups = (row, lines) => {
    const key = rowKey(row);
    const clamped = clampLines(lines, ceilingMap(row));
    setDrafts({ [key]: clamped });
    queueSave(row, { groups: groupsToSave(clamped) });
  };

  const openGroups = (row) => {
    const key = rowKey(row);
    if (openId === key) {
      setOpenId('');
      setDrafts({});
      return;
    }
    setOpenId(key);
    const lines = row.groups?.length ? linesFromGroups(row.groups) : [defaultLine(row, losCatalog)];
    setDrafts({ [key]: lines });
  };

  const needle = search.trim().toLowerCase();
  const visible = rows.filter((row) => {
    if (funding.length && !funding.some((name) => (row.funding_names || []).includes(name))) return false;
    if (!needle) return true;
    const hay = [row.fleet_id, row.reg_no, row.model, row.year].join(' ').toLowerCase();
    return hay.includes(needle);
  });
  const saveClass = saveState === 'Saved'
    ? 'bg-emerald-50 text-emerald-700'
    : saveState === 'Save failed'
      ? 'bg-red-50 text-red-700'
      : 'bg-gray-100 text-gray-500';

  return (
    <div className="h-full flex flex-col gap-3 p-4 bg-gray-50">
      <div className="flex flex-wrap items-center gap-3">
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search fleet ID / reg no...."
          className="flex-1 min-w-[220px] px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg"
        />
        <span className="px-2.5 py-1 text-xs font-semibold tracking-wide text-gray-500 bg-white border border-gray-200 rounded-full">
          Total: {rows.length}
        </span>
        {funding.length ? (
          <span className="px-2.5 py-1 text-xs font-semibold tracking-wide text-gray-500 bg-white border border-gray-200 rounded-full">
            Showing: {visible.length}
          </span>
        ) : null}
        <span className={`px-2.5 py-1 text-xs font-semibold tracking-wide rounded-full ${saveClass}`}>
          {saveState}
        </span>
        <button
          type="button"
          onClick={() => load(true)}
          className="ml-auto inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold tracking-wide text-white bg-slate-900 rounded-lg hover:bg-slate-800"
        >
          <FiRefreshCw className="w-3.5 h-3.5" />
          Refresh
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <FundingFilter names={fundingNames} selected={funding} onChange={setFunding} />
        {search || funding.length ? (
          <button
            type="button"
            onClick={() => {
              setSearch('');
              setFunding([]);
            }}
            className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
          >
            Clear
          </button>
        ) : null}
      </div>

      {duplicateIds ? (
        <p className="text-xs text-amber-700">
          The fleet list has duplicate ids. The last row for each id is shown. Ask before changing that.
        </p>
      ) : null}
      {!losLoaded ? (
        <p className="text-xs text-amber-700">The LOS list did not load. Grouping can still use this van’s own LOS. Refresh to try again.</p>
      ) : null}

      {loading ? (
        <div className="flex-1 flex items-center justify-center text-sm text-gray-500">Loading vans…</div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-4">{error}</div>
      ) : (
        <div className="flex-1 overflow-auto bg-white border border-gray-200 rounded-xl">
          <table className="min-w-full text-sm">
            <thead className="bg-white text-left text-[11px] uppercase tracking-wider text-gray-400">
              <tr>
                <th className="px-3 py-3 font-semibold">Fleet</th>
                <th className="px-3 py-3 font-semibold">Model</th>
                <th className="px-3 py-3 font-semibold">Year</th>
                <th className="px-3 py-3 font-semibold">Reg No</th>
                <th className="px-3 py-3 font-semibold">Funding</th>
                {losCatalog.map((los) => (
                  <th key={los.los_id} className="px-3 py-3 font-semibold" title={los.los_id}>
                    {los.los_title || los.los_id}
                  </th>
                ))}
                <th className="px-3 py-3 font-semibold">Groups</th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 ? (
                <tr>
                  <td colSpan={6 + losCatalog.length} className="px-3 py-8 text-center text-gray-500">No vans match.</td>
                </tr>
              ) : visible.map((row) => {
                const key = rowKey(row);
                const open = openId === key;
                const byId = {};
                (row.los_capacity || []).forEach((cell) => {
                  byId[cell.los_id] = cell;
                });
                const lines = drafts[key] || [];
                return (
                  <React.Fragment key={key}>
                    <tr className="border-t border-gray-100 align-middle">
                      <td className="px-3 py-3 text-xs text-gray-500">{row.fleet_id}</td>
                      <td className="px-3 py-3 text-xs font-semibold tracking-wide text-gray-800">{row.model || DASH}</td>
                      <td className="px-3 py-3 text-gray-600">{row.year || DASH}</td>
                      <td className="px-3 py-3 text-gray-700">{row.reg_no || DASH}</td>
                      <td className="px-3 py-3 text-xs text-gray-500 max-w-[240px]">
                        <div
                          className="cap-funding-scroll flex items-center gap-1 max-w-[220px] overflow-x-auto overflow-y-hidden whitespace-nowrap pb-1"
                          title={(row.funding_names || []).join(', ')}
                        >
                          {(row.funding_names || []).length ? (
                            row.funding_names.map((name, idx) => (
                              <span
                                key={`${name}-${idx}`}
                                className="inline-flex shrink-0 items-center rounded border border-slate-100 bg-slate-50 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600"
                              >
                                {name}
                              </span>
                            ))
                          ) : DASH}
                        </div>
                      </td>
                      {losCatalog.map((los) => {
                        const stored = byId[los.los_id] ? byId[los.los_id].max : null;
                        const value = stored === 0 || stored ? String(stored) : '';
                        return (
                          <td key={los.los_id} className="px-3 py-3">
                            <input
                              type="number"
                              min="0"
                              step="1"
                              inputMode="numeric"
                              aria-label={`${row.fleet_id} ${los.los_title || los.los_id} max`}
                              value={value}
                              placeholder="1"
                              onChange={(event) => {
                                const raw = event.target.value;
                                if (raw === '') {
                                  changeMax(row, los.los_id, null);
                                  return;
                                }
                                if (!/^\d+$/.test(raw)) return;
                                changeMax(row, los.los_id, parseInt(raw, 10));
                              }}
                              className="!w-16 h-9 px-2 py-0 text-sm text-center bg-white border border-gray-200 rounded-lg"
                            />
                          </td>
                        );
                      })}
                      <td className="px-3 py-3">
                        <button
                          type="button"
                          onClick={() => openGroups(row)}
                          className={`px-3 py-1 text-[11px] font-semibold tracking-wide rounded-full ${open ? 'bg-slate-900 text-white' : 'bg-white text-gray-700 border border-gray-300'}`}
                        >
                          Groups
                        </button>
                      </td>
                    </tr>
                    {open ? (
                      <tr className="bg-gray-50">
                        <td colSpan={6 + losCatalog.length} className="px-4 py-3">
                          <GroupEditor
                            row={row}
                            catalog={losCatalog}
                            lines={lines}
                            onChange={(next) => changeGroups(row, next)}
                          />
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
